import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, CircularProgress, useTheme, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Link, Button, Tooltip, ToggleButton, ToggleButtonGroup, Pagination } from '@mui/material';
import { alpha } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SectionCard, SectionHeader, SectionLoading, NestedPanel, SentimentBadge } from '../components/ui/Section';
import { useChartStyles, ChartTooltipCard } from '../components/ui/chartStyles';
import { useAxiosClient } from '../utils/useAxiosClient';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { format, differenceInDays, isValid } from 'date-fns';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import BrandPillars from '../components/BrandPillars';
import WordCloud from '../components/WordCloud';
import AudienceSentiment from '../components/AudienceSentiment';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell, TooltipProps as RechartsTooltipProps } from 'recharts';
import { useFilterContext } from '../context/FilterContext';

// Interface for the context provided by the Outlet (from MainLayout)
interface FilterProps {
  selectedBrands: string[];
  selectedPlatforms: string[];
  selectedPillars: string[];
  startDate: string | null;
  endDate: string | null;
}

interface KeywordItem {
  keyword: string;
  brand: string;
  frequency: number;
  relevance: number;
  sentiment: number;
}

interface SyntaxCardData {
  keywords: KeywordItem[];
}

// Keywords Syntax Card component
const KeywordSyntaxCard = ({ 
  selectedBrands, 
  selectedPillars, 
  startDate, 
  endDate,
  onKeywordClick,
  selectedKeyword
}: { 
  selectedBrands: string[], 
  selectedPillars: string[], 
  startDate: string | null, 
  endDate: string | null,
  onKeywordClick: (keyword: string) => void,
  selectedKeyword: string | null
}) => {
  const [syntaxData, setSyntaxData] = useState<SyntaxCardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const axiosClient = useAxiosClient();
  const theme = useTheme();
  const cs = useChartStyles();

  useEffect(() => {
    const fetchSyntaxData = async () => {
      setLoading(true);
      try {
        // Construct query parameters
        const params = new URLSearchParams();
        if (selectedBrands.length > 0) {
          params.append('brands', selectedBrands[0]);
        }
        selectedPillars.forEach(pillar => {
          params.append('pillars', pillar);
        });
        if (startDate) {
          params.append('start_date', startDate);
        }
        if (endDate) {
          params.append('end_date', endDate);
        }
        
        // Always add LinkedIn as platform for Employee Brand Personal Metrics
        params.append('platforms', 'linkedin');
        
        const paramString = params.toString() ? `?${params.toString()}` : '';
        console.log(`Making request to /api/personal-syntax-card${paramString}`);
        const response = await axiosClient.get(`/api/personal-syntax-card${paramString}`);
        
        // Use the live data from the API
        if (response.data && response.data.keywords && response.data.keywords.length > 0) {
          setSyntaxData(response.data);
          setError(null);
        } else {
          // If the API returns empty data, set an error and use placeholder data for demo
          console.log('API returned empty data, using placeholder data');
          setError('No keyword data found for the selected filters');
          setSyntaxData(placeholderSyntaxData);
        }
      } catch (err) {
        console.error('Error fetching syntax card data:', err);
        setError('Failed to load keyword data');
        // Still use placeholder data for demo
        setSyntaxData(placeholderSyntaxData);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSyntaxData();
  }, [axiosClient, selectedBrands, selectedPillars, startDate, endDate]);

  const keywordHeader = (
    <SectionHeader
      title="Top 100 Keywords by Relevance"
      info="The keywords in this person's posts, plotted by sentiment and relevance. Bubble size is frequency. Click a keyword in the table to filter the message preview."
    />
  );

  if (loading) {
    return (
      <>
        {keywordHeader}
        <SectionLoading height={320} />
      </>
    );
  }

  if (error && !syntaxData) {
    return (
      <>
        {keywordHeader}
        <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>{error}</Typography>
      </>
    );
  }

  // Use placeholder data for now
  const data = syntaxData?.keywords || [];

  // Check if there's data to display
  if (data.length === 0) {
    return (
      <>
        {keywordHeader}
        <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          No keywords for these filters. Try a wider date range or more pillars.
        </Typography>
      </>
    );
  }

  // Custom tooltip for the scatter plot
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <ChartTooltipCard
          title={data.keyword}
          rows={[
            { label: 'Frequency', value: data.frequency },
            { label: 'Relevance', value: data.relevance.toFixed(2) },
            { label: 'Sentiment', value: data.sentiment.toFixed(2) },
          ]}
        />
      );
    }
    return null;
  };

  // Use fixed ranges for chart domains instead of calculating from data
  const minRelevance = 0;
  const maxRelevance = 1;
  const minSentiment = -1;
  const maxSentiment = 1;

  // Diverging color by sentiment: red for negative, grey around zero, green for positive
  const getColor = (sentiment: number) => {
    if (sentiment > 0.1) return alpha(theme.ink.positive, 0.45 + Math.min(sentiment, 1) * 0.5);
    if (sentiment < -0.1) return alpha(theme.ink.negative, 0.45 + Math.min(-sentiment, 1) * 0.5);
    return alpha(theme.palette.text.secondary, 0.6);
  };

  return (
    <>
      {keywordHeader}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {/* Left side: Scatter plot */}
        <NestedPanel sx={{ height: 420 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{ top: 8, right: 12, bottom: 24, left: 4 }}
            >
              <CartesianGrid stroke={cs.line} strokeDasharray="3 4" />
              <XAxis
                {...cs.xAxis}
                type="number"
                dataKey="sentiment"
                name="Sentiment"
                domain={[minSentiment, maxSentiment]}
                ticks={[-1, -0.5, 0, 0.5, 1]}
                label={{ value: 'Sentiment', position: 'insideBottom', offset: -16, style: { fontSize: 12, fill: cs.muted } }}
              />
              <YAxis
                {...cs.yAxis}
                type="number"
                dataKey="relevance"
                name="Relevance"
                width={48}
                domain={[minRelevance, maxRelevance]}
                label={cs.axisLabel('Relevance')}
              />
              <ZAxis
                type="number"
                dataKey="frequency"
                range={[40, 220]}
                name="Frequency"
              />
              <RechartsTooltip
                cursor={{ strokeDasharray: '3 3', stroke: cs.muted }}
                content={CustomTooltip}
              />
              <Scatter name="Keywords" data={data} isAnimationActive={false}>
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getColor(entry.sentiment)}
                    stroke={selectedKeyword === entry.keyword ? theme.palette.primary.main : 'none'}
                    strokeWidth={2}
                    cursor="pointer"
                    onClick={() => onKeywordClick(entry.keyword)}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </NestedPanel>

        {/* Right side: Table */}
        <TableContainer sx={(t) => ({ maxHeight: 420, border: `1px solid ${t.palette.divider}`, borderRadius: '10px' })}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: 'background.paper' } }}>
                <TableCell>Keyword</TableCell>
                <TableCell align="right">Frequency</TableCell>
                <TableCell align="right">Relevance</TableCell>
                <TableCell align="center">Sentiment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item, index) => (
                <TableRow
                  key={index}
                  hover
                  selected={selectedKeyword === item.keyword}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onKeywordClick(item.keyword)}
                >
                  <TableCell sx={{ fontWeight: selectedKeyword === item.keyword ? 600 : 400 }}>
                    {item.keyword}
                  </TableCell>
                  <TableCell align="right">{item.frequency}</TableCell>
                  <TableCell align="right">{item.relevance.toFixed(2)}</TableCell>
                  <TableCell align="center"><SentimentBadge value={item.sentiment} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

// Placeholder data for the syntax card
const placeholderSyntaxData: SyntaxCardData = {
  keywords: [
    { keyword: 'vinataea', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.52, sentiment: 0.9 },
    { keyword: 'adolfo', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.45, sentiment: 0.9 },
    { keyword: 'capital', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.42, sentiment: 0.6 },
    { keyword: '#empresaelsa', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.38, sentiment: 0.4 },
    { keyword: 'sigan', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.35, sentiment: 0.9 },
    { keyword: 'privado', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.34, sentiment: 0.6 },
    { keyword: 'krealo', brand: 'Adolfo Vinatea', frequency: 2, relevance: 0.34, sentiment: 0.65 },
    { keyword: 'elsa', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.31, sentiment: 0.4 },
    { keyword: 'equipo', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.29, sentiment: 0.9 },
    { keyword: 'éxitos', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.27, sentiment: 0.9 },
    { keyword: 'espacios', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.22, sentiment: 0.4 },
    { keyword: 'venture', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.22, sentiment: 0.6 },
    { keyword: 'corporate', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.22, sentiment: 0.6 },
    { keyword: 'felicitaciones', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.22, sentiment: 0.9 },
    { keyword: 'ecosistema', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.20, sentiment: 0.6 },
    { keyword: 'impulsando', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.20, sentiment: 0.6 },
    { keyword: 'emprendedor', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.19, sentiment: 0.6 },
    { keyword: 'reforzando', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.18, sentiment: 0.4 },
    { keyword: 'diagnóstico', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.18, sentiment: 0.4 },
    { keyword: 'implementando', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.16, sentiment: 0.4 },
    { keyword: 'apostamos', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.16, sentiment: 0.4 },
    { keyword: 'acoso', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.16, sentiment: 0.4 },
    { keyword: 'hoyos', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.16, sentiment: 0.6 },
    { keyword: 'colcapital', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.16, sentiment: 0.6 },
    { keyword: 'zabala', brand: 'Adolfo Vinatea', frequency: 1, relevance: 0.16, sentiment: 0.6 }
  ]
};

interface MetricsData {
  current_metrics: {
    posts_count: number;
    likes_count: number;
    comments_count: number;
    reposts_count: number;
    avg_engagement: number;
    follower_count: number;
    influencer_index: number;
    influencer_rank: number;
  };
  percent_changes: {
    posts_count: number;
    likes_count: number;
    comments_count: number;
    reposts_count: number;
    avg_engagement: number;
    follower_count: number;
    influencer_index: number;
    influencer_rank: number;
  };
  date_range: {
    current_start: string;
    current_end: string;
    previous_start: string;
    previous_end: string;
  };
  wordcloud_data?: Array<{ text: string; value: number }>;
  monthly_metric_performance?: {
    [month: string]: {
      average_engagement: { value: number; change: number };
      comments: { value: number; change: number };
      likes: { value: number; change: number };
      posts: { value: number; change: number };
      shares: { value: number; change: number };
    }
  };
}

interface InfluencerMetric {
  brand: string;
  influencer_index: number;
  previous_index: number;
  percent_change: number;
  rank: number;
}

interface InfluencerData {
  influencer_metrics: InfluencerMetric[];
  date_range: {
    current_start: string;
    current_end: string;
    previous_start: string;
    previous_end: string;
  };
}

interface MessagePreviewItem {
  brand: string;
  message: string;
  post_link: string;
  type: 'Post' | 'Comment';
  likes: number;
  post_datetime?: string;
}

// Message Preview table component
const MessagePreview = ({ 
  selectedBrands, 
  selectedPillars, 
  startDate, 
  endDate,
  keywordFilter
}: { 
  selectedBrands: string[], 
  selectedPillars: string[], 
  startDate: string | null, 
  endDate: string | null,
  keywordFilter: string | null
}) => {
  const [allMessages, setAllMessages] = useState<MessagePreviewItem[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<MessagePreviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const axiosClient = useAxiosClient();
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter toggles
  const [showPosts, setShowPosts] = useState(true);
  const [showComments, setShowComments] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        // Construct query parameters
        const params = new URLSearchParams();
        if (selectedBrands.length > 0) {
          params.append('brands', selectedBrands[0]);
        }
        selectedPillars.forEach(pillar => {
          params.append('pillars', pillar);
        });
        if (startDate) {
          params.append('start_date', startDate);
        }
        if (endDate) {
          params.append('end_date', endDate);
        }
        
        // Always add LinkedIn as platform for Employee Brand Personal Metrics
        params.append('platforms', 'linkedin');
        
        const paramString = params.toString() ? `?${params.toString()}` : '';
        const response = await axiosClient.get(`/api/message-preview${paramString}`);
        
        // Store all messages for client-side filtering
        setAllMessages(response.data.messages);
        setError(null);
      } catch (err) {
        console.error('Error fetching message preview data:', err);
        setError('Failed to load message preview data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMessages();
  }, [axiosClient, selectedBrands, selectedPillars, startDate, endDate]);

  // Apply filters whenever the filter toggles, all messages, or keyword filter change
  useEffect(() => {
    // Filter messages based on type toggles and keyword
    const newFilteredMessages = allMessages.filter(message => {
      // First apply type filter
      const passesTypeFilter = 
        (message.type === 'Post' && showPosts) || 
        (message.type === 'Comment' && showComments);
      
      // Then apply keyword filter if present
      const passesKeywordFilter = !keywordFilter || 
        message.message.toLowerCase().includes(keywordFilter.toLowerCase());
      
      return passesTypeFilter && passesKeywordFilter;
    });
    
    setFilteredMessages(newFilteredMessages);
    // Reset to first page when filters change
    setPage(0);
  }, [allMessages, showPosts, showComments, keywordFilter]);

  // Handle filter toggle
  const handleToggleFilter = (type: 'Post' | 'Comment') => {
    if (type === 'Post') {
      setShowPosts(!showPosts);
    } else {
      setShowComments(!showComments);
    }
  };

  // Handle page change
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  if (loading) {
    return <SectionLoading height={240} />;
  }

  if (error) {
    return <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>{error}</Typography>;
  }

  if (allMessages.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        No messages for these filters.
      </Typography>
    );
  }

  // Calculate pagination
  const paginatedMessages = filteredMessages.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  // Calculate total pages
  const totalPages = Math.ceil(filteredMessages.length / rowsPerPage);

  return (
    <Box>
      {/* Filter buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size="small"
          aria-label="message types"
          value={[showPosts ? 'Post' : null, showComments ? 'Comment' : null].filter(Boolean) as string[]}
          onChange={(_e, next: string[]) => {
            if (next.includes('Post') !== showPosts) handleToggleFilter('Post');
            if (next.includes('Comment') !== showComments) handleToggleFilter('Comment');
          }}
        >
          <ToggleButton value="Post">Posts</ToggleButton>
          <ToggleButton value="Comment">Comments</ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
          {keywordFilter
            ? `${filteredMessages.length} of ${allMessages.length} messages mention "${keywordFilter}"`
            : `${filteredMessages.length} of ${allMessages.length} messages`}
        </Typography>
      </Box>

      <TableContainer sx={{ mx: { xs: -2, md: -3 }, width: 'auto', px: { xs: 1, md: 1.5 } }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { px: 1.5, py: 1 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Message</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Likes</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Link</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMessages.map((message, index) => (
              <TableRow key={index} hover>
                <TableCell sx={{ maxWidth: 0, width: '62%' }}>
                  <Tooltip title={message.message} placement="top-start">
                    <Typography noWrap variant="body2">
                      {message.message}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>{message.type}</TableCell>
                <TableCell align="right">{(message.likes || 0).toLocaleString()}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                  {message.post_datetime ? format(new Date(message.post_datetime), 'MMM d, yyyy') : '-'}
                </TableCell>
                <TableCell>
                  <Link
                    href={message.post_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
                  >
                    View <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(_e, value) => handleChangePage(null, value - 1)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

// A component for displaying monthly metrics in a table
const MonthlyMetricsCard = ({ monthlyMetrics }: { monthlyMetrics: MetricsData['monthly_metric_performance'] }) => {
  const theme = useTheme();
  
  if (!monthlyMetrics || Object.keys(monthlyMetrics).length === 0) {
    return null;
  }
  
  // Get color based on change value
  const getChangeColor = (changeValue: number) => {
    if (changeValue > 0) return theme.palette.success.main;
    if (changeValue < 0) return theme.palette.error.main;
    return theme.palette.text.secondary;
  };
  
  // Get trend icon based on change
  const getTrendIcon = (changeValue: number) => {
    if (changeValue > 0) return <TrendingUp fontSize="small" />;
    if (changeValue < 0) return <TrendingDown fontSize="small" />;
    return <TrendingFlat fontSize="small" />;
  };
  
  // Generate arrow and percent change display
  const renderChangeValue = (change: number) => {
    const color = getChangeColor(change);
    const trendIcon = getTrendIcon(change);
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', color, '& svg': { fontSize: 16 } }}>
        {trendIcon}
        <Typography variant="caption" sx={{ ml: 0.5, color: 'inherit', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {Math.abs(change).toFixed(1)}%
        </Typography>
      </Box>
    );
  };
  
  // Capitalize first letter of a string
  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };
  
  // Sort months chronologically (oldest to newest)
  const sortMonths = (months: string[]) => {
    return months.sort((a, b) => {
      // Parse month names to dates for proper chronological sorting
      try {
        const dateA = new Date(a);
        const dateB = new Date(b);
        
        // If valid dates, compare them
        if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
          return dateA.getTime() - dateB.getTime();
        }
        
        // Fallback to the previous method if date parsing fails
        const [monthA, yearA] = a.split('-');
        const [monthB, yearB] = b.split('-');
        
        // Convert month names to month numbers (0-11)
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December'];
        
        const monthNumA = monthNames.indexOf(monthA);
        const monthNumB = monthNames.indexOf(monthB);
        
        // Compare years first
        if (yearA !== yearB) {
          return parseInt(yearA) - parseInt(yearB); // Oldest year first
        }
        
        // If same year, compare months
        return monthNumA - monthNumB; // Oldest month first
      } catch (error) {
        console.error('Error sorting months:', error);
        return 0;
      }
    });
  };
  
  // Get months in chronological order (oldest first)
  const months = sortMonths(Object.keys(monthlyMetrics)).slice(0, 4);
  
  // Metric names with proper capitalization
  const metricNames = {
    'average_engagement': 'Average engagement',
    'comments': 'Comments',
    'likes': 'Likes',
    'posts': 'Posts',
    'shares': 'Shares'
  };
  
  return (
    <SectionCard>
      <SectionHeader
        title="Monthly Metric Performance"
        description="Each month compared with the 30 days before it."
      />

      <TableContainer>
        <Table size="small" sx={{ '& .MuiTableCell-body': { py: 1.25 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Metrics</TableCell>
              {months.map((month) => (
                <TableCell key={month} align="center">{month}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Average Engagement Row */}
            <TableRow>
              <TableCell component="th" scope="row">{metricNames.average_engagement}</TableCell>
              {months.map((month) => (
                <TableCell key={`avg-${month}`} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{monthlyMetrics[month].average_engagement.value}</Typography>
                    {renderChangeValue(monthlyMetrics[month].average_engagement.change)}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
            
            {/* Comments Row */}
            <TableRow>
              <TableCell component="th" scope="row">{metricNames.comments}</TableCell>
              {months.map((month) => (
                <TableCell key={`comments-${month}`} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{monthlyMetrics[month].comments.value}</Typography>
                    {renderChangeValue(monthlyMetrics[month].comments.change)}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
            
            {/* Likes Row */}
            <TableRow>
              <TableCell component="th" scope="row">{metricNames.likes}</TableCell>
              {months.map((month) => (
                <TableCell key={`likes-${month}`} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{monthlyMetrics[month].likes.value}</Typography>
                    {renderChangeValue(monthlyMetrics[month].likes.change)}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
            
            {/* Posts Row */}
            <TableRow>
              <TableCell component="th" scope="row">{metricNames.posts}</TableCell>
              {months.map((month) => (
                <TableCell key={`posts-${month}`} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{monthlyMetrics[month].posts.value}</Typography>
                    {renderChangeValue(monthlyMetrics[month].posts.change)}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
            
            {/* Shares Row */}
            <TableRow>
              <TableCell component="th" scope="row">{metricNames.shares}</TableCell>
              {months.map((month) => (
                <TableCell key={`shares-${month}`} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{monthlyMetrics[month].shares.value}</Typography>
                    {renderChangeValue(monthlyMetrics[month].shares.change)}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </SectionCard>
  );
};

const MetricCard = ({ title, value, change, icon }: { title: string; value: number | string; change: number; icon?: React.ReactNode }) => {
  const theme = useTheme();
  
  // Determine color based on change value
  const getChangeColor = (changeValue: number) => {
    if (changeValue > 0) return theme.palette.success.main;
    if (changeValue < 0) return theme.palette.error.main;
    return theme.palette.text.secondary;
  };
  
  // Get trend icon based on change
  const getTrendIcon = (changeValue: number) => {
    if (changeValue > 0) return <TrendingUp fontSize="small" />;
    if (changeValue < 0) return <TrendingDown fontSize="small" />;
    return <TrendingFlat fontSize="small" />;
  };
  
  const color = getChangeColor(change);
  const trendIcon = getTrendIcon(change);
  
  return (
    <NestedPanel sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 2, md: 2.25 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" component="p" sx={{ my: 0.75, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, color, '& svg': { fontSize: 18 } }}>
        {trendIcon}
        <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
          {Math.abs(change)}% {change > 0 ? 'up' : change < 0 ? 'down' : 'no change'}
        </Typography>
        <Typography variant="body2" color="text.secondary">vs previous period</Typography>
      </Box>
    </NestedPanel>
  );
};

const EmployeeBrandPersonalMetrics: React.FC = () => {
  const [metricsData, setMetricsData] = useState<MetricsData | null>(null);
  const [influencerData, setInfluencerData] = useState<InfluencerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const axiosClient = useAxiosClient();
  const [searchParams] = useSearchParams();
  const [wordcloudData, setWordcloudData] = useState<Array<{ text: string; value: number }>>([]);
  const { availableBrands } = useFilterContext();
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);
  
  // Get filter data from outlet context (like GeneralMetrics does)
  const { 
    startDate: contextStartDate, 
    endDate: contextEndDate, 
    selectedBrands: contextSelectedBrands, 
    selectedPillars: contextSelectedPillars
  } = useOutletContext<FilterProps>();
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Use context data first, fall back to search params
        const brands = contextSelectedBrands?.[0] || searchParams.get('brands');
        const pillars = contextSelectedPillars?.length > 0 ? contextSelectedPillars : searchParams.getAll('pillars');
        const startDate = contextStartDate || searchParams.get('start_date');
        const endDate = contextEndDate || searchParams.get('end_date');
        
        // Log parameters for debugging
        console.log("Date parameters:", { 
          startDate, 
          endDate, 
          contextStartDate, 
          contextEndDate,
          brands,
          pillars 
        });
        
        // Construct query parameters
        const params = new URLSearchParams();
        if (brands) {
          params.append('brands', brands);
        } else if (availableBrands.length > 0) {
          params.append('brands', availableBrands[0].id);
        }
        if (pillars && pillars.length > 0) {
          pillars.forEach(pillar => {
            params.append('pillars', pillar);
          });
        }
        if (startDate) {
          params.append('start_date', startDate);
        }
        if (endDate) {
          params.append('end_date', endDate);
        }
        
        const paramString = params.toString() ? `?${params.toString()}` : '';
        console.log("Base params:", paramString);
        
        // Make API calls for metrics data
        // 1. Performance Summary: period comparison (default behavior)
        // 2. Date-range specific data: wordcloud and monthly metrics without comparison
        const summaryParams = new URLSearchParams(params);
        summaryParams.append('period_comparison', 'true');
        
        const dateRangeParams = new URLSearchParams(params);
        dateRangeParams.append('period_comparison', 'false');
        
        const [summaryResponse, dateRangeResponse, influencerResponse] = await Promise.all([
          axiosClient.get(`/api/personal-branding-metrics?${summaryParams.toString()}`),
          axiosClient.get(`/api/personal-branding-metrics?${dateRangeParams.toString()}`),
          axiosClient.get(`/api/personal-branding-influencer${paramString}`)
        ]);
        
        // Combine the responses: summary metrics from summaryResponse, wordcloud/monthly from dateRangeResponse
        const combinedMetricsData = {
          ...summaryResponse.data,
          wordcloud_data: dateRangeResponse.data.wordcloud_data,
          monthly_metric_performance: dateRangeResponse.data.monthly_metric_performance
        };
        
        setMetricsData(combinedMetricsData);
        setInfluencerData(influencerResponse.data);
        setError(null);
        
        // Set the wordcloud data from the date-range specific response
        if (dateRangeResponse.data.wordcloud_data) {
          setWordcloudData(dateRangeResponse.data.wordcloud_data);
        }
      } catch (err) {
        console.error('Error fetching personal branding data:', err);
        setError('Failed to load metrics data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [axiosClient, searchParams, availableBrands, contextStartDate, contextEndDate, contextSelectedBrands, contextSelectedPillars]); // Re-fetch when filters change
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  
  if (!metricsData || !influencerData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No metrics data available</Typography>
      </Box>
    );
  }
  
  const formatDate = (dateString: string) => {
    try {
      // Create date without timezone conversion (treat as UTC, then format)
      const [year, month, day] = dateString.split('-').map(Number);
      return format(new Date(year, month - 1, day), 'MMM d, yyyy');
    } catch (e) {
      return dateString;
    }
  };
  
  const getDateRangeSummary = (startDate: string, endDate: string, prevStartDate: string, prevEndDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (!isValid(start) || !isValid(end)) {
      return 'Current period';
    }
    
    const daySpan = differenceInDays(end, start) + 1;
    
    if (daySpan <= 1) {
      return `${formatDate(startDate)}`;
    } else if (daySpan <= 31) {
      return `${daySpan}-day period (${formatDate(startDate)} to ${formatDate(endDate)})`;
    } else {
      return `Selected period: ${formatDate(startDate)} to ${formatDate(endDate)}`;
    }
  };
  
  const { current_metrics, percent_changes, date_range } = metricsData;
  
  // Get the first influencer metric (which should be the filtered person if specified)
  const currentBrandMetric = influencerData.influencer_metrics.length > 0 
    ? influencerData.influencer_metrics[0] 
    : null;
  
  // Generate summary text for the date range
  const dateRangeSummary = getDateRangeSummary(
    date_range.current_start, 
    date_range.current_end,
    date_range.previous_start,
    date_range.previous_end
  );
  
  const compareSummary = `Comparing with previous equal period (${formatDate(date_range.previous_start)} to ${formatDate(date_range.previous_end)})`;
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* First Card: Metrics Summary */}
      <SectionCard>
        <SectionHeader
          title="LinkedIn Performance Summary"
          description={`${dateRangeSummary}. ${compareSummary}`}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MetricCard 
              title="Total Posts" 
              value={current_metrics.posts_count} 
              change={percent_changes.posts_count}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MetricCard 
              title="Likes" 
              value={current_metrics.likes_count?.toLocaleString() || '0'} 
              change={percent_changes.likes_count || 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MetricCard 
              title="Comments" 
              value={current_metrics.comments_count?.toLocaleString() || '0'} 
              change={percent_changes.comments_count || 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MetricCard 
              title="Reposts" 
              value={current_metrics.reposts_count?.toLocaleString() || '0'} 
              change={percent_changes.reposts_count || 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MetricCard 
              title="Avg. Engagement" 
              value={current_metrics.avg_engagement?.toLocaleString() || '0'} 
              change={percent_changes.avg_engagement || 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <NestedPanel sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 2, md: 2.25 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Followers
              </Typography>
              <Typography variant="h5" component="p" sx={{ my: 1, color: 'text.secondary' }}>
                Coming soon
              </Typography>
            </NestedPanel>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MetricCard 
              title="Influencer Index" 
              value={currentBrandMetric?.influencer_index.toFixed(1) || '0.0'} 
              change={currentBrandMetric?.percent_change || 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MetricCard 
              title="Influencer Rank" 
              value={`#${currentBrandMetric?.rank || 0}`} 
              change={(currentBrandMetric?.percent_change || 0) * -1} // Reverse change since lower rank is better
            />
          </Grid>
        </Grid>
      </SectionCard>

      {/* Performance Across Pillars Section with WordCloud */}
      <Grid container spacing={3} alignItems="stretch">
        {/* Left side: Performance Across Pillars */}
        <Grid item xs={12} md={7}>
          <Box sx={{ height: '100%', '& > section': { height: '100%' } }}>
            <BrandPillars 
              selectedBrands={contextSelectedBrands?.length > 0 ? contextSelectedBrands : (searchParams.get('brands') ? [searchParams.get('brands') as string] : availableBrands.length > 0 ? [availableBrands[0].id] : [])}
              selectedPillars={contextSelectedPillars?.length > 0 ? contextSelectedPillars : searchParams.getAll('pillars')}
              startDate={contextStartDate || searchParams.get('start_date') || null}
              endDate={contextEndDate || searchParams.get('end_date') || null}
              hideLegend={true}
              isPersonalPage={true}
            />
          </Box>
        </Grid>
        
        {/* Right side: Word Cloud */}
        <Grid item xs={12} md={5}>
          <WordCloud 
            data={wordcloudData}
            height={400}
            loading={loading}
          />
        </Grid>
      </Grid>
      
      {/* Audience Sentiment Section */}
      <AudienceSentiment
        selectedBrands={contextSelectedBrands?.length > 0 ? contextSelectedBrands : (searchParams.get('brands') ? [searchParams.get('brands') as string] : availableBrands.length > 0 ? [availableBrands[0].id] : [])}
        selectedPillars={contextSelectedPillars?.length > 0 ? contextSelectedPillars : searchParams.getAll('pillars')}
        startDate={contextStartDate || searchParams.get('start_date') || null}
        endDate={contextEndDate || searchParams.get('end_date') || null}
      />
      
      {/* Monthly Metrics Card */}
      {metricsData.monthly_metric_performance && (
        <MonthlyMetricsCard monthlyMetrics={metricsData.monthly_metric_performance} />
      )}

      {/* Top 100 Most Relevant Keywords Card */}
      <Box>
        <SectionCard>
          <KeywordSyntaxCard
            selectedBrands={contextSelectedBrands?.length > 0 ? contextSelectedBrands : (searchParams.get('brands') ? [searchParams.get('brands') as string] : availableBrands.length > 0 ? [availableBrands[0].id] : [])}
            selectedPillars={contextSelectedPillars?.length > 0 ? contextSelectedPillars : searchParams.getAll('pillars')}
            startDate={contextStartDate || searchParams.get('start_date') || null}
            endDate={contextEndDate || searchParams.get('end_date') || null}
            onKeywordClick={(keyword) => setSelectedKeyword(keyword === selectedKeyword ? null : keyword)}
            selectedKeyword={selectedKeyword}
          />
        </SectionCard>
      </Box>

      {/* Message Preview Card */}
      <Box>
        <SectionCard>
          <SectionHeader
            title={selectedKeyword ? `Messages mentioning "${selectedKeyword}"` : 'Message Preview'}
            description={selectedKeyword ? undefined : 'All posts and comments from the selected dates.'}
            actions={selectedKeyword ? (
              <Button size="small" onClick={() => setSelectedKeyword(null)} variant="outlined">
                Clear keyword
              </Button>
            ) : undefined}
          />
                    
          <MessagePreview
            selectedBrands={contextSelectedBrands?.length > 0 ? contextSelectedBrands : (searchParams.get('brands') ? [searchParams.get('brands') as string] : availableBrands.length > 0 ? [availableBrands[0].id] : [])}
            selectedPillars={contextSelectedPillars?.length > 0 ? contextSelectedPillars : searchParams.getAll('pillars')}
            startDate={contextStartDate || searchParams.get('start_date') || null}
            endDate={contextEndDate || searchParams.get('end_date') || null}
            keywordFilter={selectedKeyword}
          />
        </SectionCard>
      </Box>
    </Box>
  );
};

export default EmployeeBrandPersonalMetrics; 